"use client";

import Carousel from "./common/Carousel";
import FadeDiv from "./common/FadeDiv";
import GailComparisonCard from "./GailComparisonCard";
import Image from "next/image";
import React from "react";
import SectionTitle from "./common/SectionTitle";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

type GailComparisonProps = {
  title: string;
  description: string;
  image: React.ReactNode;
}[];

const GailComparison: React.FC = () => {
  const { t } = useTranslation();

  const features: GailComparisonProps = [
    {
      title: t("gailVersus.compliant.title"),
      description: t("gailVersus.compliant.description"),
      image: (
        <Image
          src="/images/gail_versus_compliant.webp"
          alt="Insurance"
          width={2440}
          height={1524}
        />
      ),
    },
    {
      title: t("gailVersus.guardrails.title"),
      description: t("gailVersus.guardrails.description"),
      image: (
        <Image
          src="/images/gail_versus_guardrails.webp"
          alt="Insurance"
          width={2440}
          height={1524}
        />
      ),
    },
    {
      title: t("gailVersus.accuracy.title"),
      description: t("gailVersus.accuracy.description"),
      image: (
        <Image
          src="/images/gail_versus_accuracy.webp"
          alt="Insurance"
          width={2440}
          height={1524}
        />
      ),
    },
    {
      title: t("gailVersus.private.title"),
      description: t("gailVersus.private.description"),
      image: (
        <Image
          src="/images/gail_versus_private.webp"
          alt="Insurance"
          width={2440}
          height={1524}
        />
      ),
    },
  ];

  return (
    <div>
      <FadeDiv className={clsx("md:hidden pb-6 bg-gail-primary text-white")}>
        <SectionTitle
          title={t("gailVersus.title")}
          className="!pb-3 !text-[40px] !px-0"
        />
        <p
          className={clsx(
            "text-center",
            "mx-auto",
            "px-8",
            "text-[19px] leading-tight"
          )}
        >
          {t("gailVersus.description")}
        </p>
        <Carousel
          itemWidth="100%"
          invertArrowColor={true}
          itemClassName="border-none !p-0 !m-0"
          items={features.map((feature, index) => (
            <GailComparisonCard
              key={index}
              title={feature.title}
              description={feature.description}
              image={feature.image}
              imageOnLeft={index % 2 === 0}
              disableFade
            />
          ))}
        />
      </FadeDiv>
      <div
        className={clsx(
          "mt-12",
          "pb-12",
          "md:mt-16",
          "border-l",
          "border-r",
          "border-gray-200",
          "hidden",
          "md:block",
          "max-w-8xl",
          "mx-auto"
        )}
      >
        <hr
          className={clsx("absolute", "w-screen", "left-0", "border-gray-200")}
        />
        <FadeDiv className={clsx("pt-16", "pb-16")}>
          <SectionTitle title={t("gailVersus.title")} className="!p-0" />
          <p
            className={clsx(
              "text-center md:text-xl",
              "max-w-lg",
              "mx-auto",
              "mt-4"
            )}
          >
            {t("gailVersus.description")}
          </p>
        </FadeDiv>
        <div className={clsx("grid", "gap-8")}>
          {features.map((feature, index) => (
            <GailComparisonCard
              key={index}
              title={feature.title}
              description={feature.description}
              image={feature.image}
              imageOnLeft={index % 2 === 0}
            />
          ))}
        </div>
      </div>
      <hr
        className={clsx("absolute", "w-screen", "left-0", "border-gray-200")}
      />
    </div>
  );
};

export default GailComparison;
